import { useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileUp, Trash2, Loader2 } from "lucide-react";
import { HelpHint } from "@/components/HelpHint";

export default function LFPdfImport({ onApply }) {
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [instructions, setInstructions] = useState("");
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState(null);
  const [pages, setPages] = useState([]);
  const [masterId, setMasterId] = useState(null);
  const [zoom, setZoom] = useState(null);
  const thumbFor = (pg) => (pages.find((p) => p.index === pg) || {}).thumb;

  const parse = async () => {
    if (!files.length) { toast.error("Choose one or more PDFs first"); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      fd.append("instructions", instructions);
      const { data } = await api.post("/lf/parse-sizes", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (!data.sizes?.length) toast.error("No sizes detected — add instructions or enter manually");
      setRows(data.sizes || []);
      setPages(data.pages || []);
      setMasterId(data.master_pdf_id || null);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setLoading(false);
  };
  const upd = (i, k, v) => setRows(rows.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const rm = (i) => setRows(rows.filter((_, idx) => idx !== i));
  const apply = (replace) => {
    const cleaned = (rows || []).filter((r) => +r.width > 0 && +r.height > 0)
      .map((r) => ({ width: +r.width, height: +r.height, qty: Math.max(1, parseInt(r.qty) || 1), name: r.name || "", page: (typeof r.page === "number" ? r.page : null), diecut: !!r.diecut, laminate: !!r.laminate, transfer_tape: !!r.transfer_tape }));
    if (!cleaned.length) { toast.error("Nothing to add"); return; }
    onApply(cleaned, replace, pages, masterId);
    toast.success(`${replace ? "Replaced with" : "Added"} ${cleaned.length} size${cleaned.length > 1 ? "s" : ""}`);
    setOpen(false); setRows(null); setFiles([]); setInstructions(""); setPages([]); setMasterId(null);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="lf-import-pdf-btn" size="sm" variant="outline" className="rounded-lg h-9 text-xs"><FileUp size={14} className="mr-1" />Import PDF</Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl" data-testid="lf-import-dialog">
        <DialogHeader><DialogTitle>Import sizes from PDF</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <input data-testid="lf-import-file" type="file" accept="application/pdf" multiple onChange={(e) => setFiles(Array.from(e.target.files || []))} className="text-sm block w-full" />
          {files.length > 1 && <p className="text-[11px] text-slate-500">{files.length} PDFs will be merged into one master (pages numbered in order).</p>}
          <div className="space-y-1">
            <div className="flex items-center gap-1">
              <label className="text-[11px] font-mono uppercase text-slate-500">Instructions (optional)</label>
              <HelpHint side="right">Type plain-English notes to guide the import. You can set <b>quantities & sizes</b> ("6 of 30x10, 10 of 24x18"), and request <b>finishing for the pieces</b> &mdash; <b>lamination</b>, <b>die-cut</b> or <b>transfer tape</b> (e.g. "add lamination and die-cut to all pieces"). Finishing you name here is pre-checked on every imported row.</HelpHint>
            </div>
            <Textarea data-testid="lf-import-instructions" value={instructions} onChange={(e) => setInstructions(e.target.value)} placeholder={"e.g. 6 of 30x10, 10 of 24x18\nAdd lamination and die-cut to all pieces"} className="rounded-lg text-sm" rows={2} />
            <p className="text-[11px] text-slate-400 leading-snug">Tell the reader what you want: quantities/sizes and finishing (lamination, die-cut, transfer tape). Finishing is pre-checked on each imported row.</p>
          </div>
          <Button data-testid="lf-import-parse" onClick={parse} disabled={loading} className="rounded-lg w-full">
            {loading ? <><Loader2 size={15} className="mr-2 animate-spin" />Reading PDF…</> : "Read PDF"}
          </Button>
          {rows && (
            <div className="border border-slate-200 rounded-lg p-3 max-h-72 overflow-auto" data-testid="lf-import-review">
              <div className="grid grid-cols-12 gap-2 text-[10px] font-mono uppercase text-slate-400 mb-1"><span className="col-span-2">W in</span><span className="col-span-2">H in</span><span className="col-span-2">Qty</span><span className="col-span-5">Name</span><span /></div>
              {rows.map((r, i) => (
                <div key={i} data-testid="lf-import-row" className={`grid grid-cols-12 gap-2 items-center mb-1 ${r.confidence === "low" ? "bg-amber-50 rounded" : ""}`}>
                  <Input className="col-span-2 h-8 num rounded" type="number" value={r.width} onChange={(e) => upd(i, "width", e.target.value)} />
                  <Input className="col-span-2 h-8 num rounded" type="number" value={r.height} onChange={(e) => upd(i, "height", e.target.value)} />
                  <Input className="col-span-2 h-8 num rounded" type="number" value={r.qty} onChange={(e) => upd(i, "qty", e.target.value)} />
                  <span className="col-span-5 flex items-center gap-1.5 text-xs text-slate-500 min-w-0" title={r.name}>
                    {typeof r.page === "number" && thumbFor(r.page) && <img src={thumbFor(r.page)} alt="" onClick={() => setZoom(thumbFor(r.page))} title="Click to zoom" className="h-7 w-7 object-contain rounded border border-slate-200 bg-white shrink-0 cursor-zoom-in hover:ring-2 hover:ring-[#2495D3]" data-testid={`lf-import-thumb-${i}`} />}
                    <span className="truncate">{r.name}{r.confidence === "low" ? " ⚠︎" : ""}</span>
                  </span>
                  <button className="col-span-1 text-slate-400 hover:text-red-500" onClick={() => rm(i)}><Trash2 size={14} /></button>
                </div>
              ))}
              {rows.length === 0 && <div className="text-xs text-slate-400 text-center py-3">No sizes detected.</div>}
            </div>
          )}
        </div>
        {rows && rows.length > 0 && (
          <DialogFooter className="gap-2 items-center">
            <span className="inline-flex items-center gap-1">
              <Button data-testid="lf-import-replace" variant="outline" className="rounded-lg" onClick={() => apply(true)}>Replace sizes</Button>
              <HelpHint side="top"><b>Replace sizes</b> clears the size rows you have now and keeps ONLY the imported ones. Use it when the imported list is the whole job.</HelpHint>
            </span>
            <span className="inline-flex items-center gap-1">
              <Button data-testid="lf-import-add" className="rounded-lg" onClick={() => apply(false)}>Add to sizes</Button>
              <HelpHint side="top"><b>Add to sizes</b> appends the imported rows to your existing size rows &mdash; nothing you already entered is removed.</HelpHint>
            </span>
          </DialogFooter>
        )}
        {zoom && (
          <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-6 cursor-zoom-out" onClick={() => setZoom(null)} data-testid="lf-import-zoom">
            <img src={zoom} alt="artwork preview" className="max-h-[88vh] max-w-[88vw] rounded-lg shadow-2xl bg-white" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
